import { Emitter } from "@core/events/emitter";
import type { IPermissionsModel } from "@models/permissions/IPermissionsModel";
import type { IUndoRedo } from "@models/project/IUndoRedo";
import type {
  IToolbarModel,
  SelectionMode,
  ToolId,
  TransformMode,
  TransformSpace,
} from "./IToolbarModel";
import { K_FALLBACK_TOOL, isToolbarToolAllowed, isUndoAllowed } from "./toolPermissions";

/**
 * `InMemoryToolbarModel` — placeholder implementation of
 * {@link IToolbarModel}. Tracks the active tool id and re-emits invoked
 * command names. Real per-tool behavior (selection, laser, slice, …) lands
 * in M9 with a richer `ToolsModel` that talks to the runtime + gizmos.
 *
 * It also OWNS the rule that a tool the user may not use is never armed:
 * {@link setActiveTool} refuses one, and a permission change disarms one back
 * to the pointer. Both used to live in `useToolbar`, which made them depend on
 * a panel holding a `useToolbar` being mounted — during a workflow run most are
 * not, so a tool armed before Play stayed live under the learner (AI-1426).
 */
export class InMemoryToolbarModel implements IToolbarModel {
  readonly onActiveToolChanged = new Emitter<ToolId>();
  readonly onTransformModeChanged = new Emitter<TransformMode>();
  readonly onTransformSpaceChanged = new Emitter<TransformSpace>();
  readonly onSelectionModeChanged = new Emitter<SelectionMode>();
  readonly onCommandInvoked = new Emitter<string>();

  private m_activeTool: ToolId = "select";
  private m_transformMode: TransformMode = "translate";
  private m_transformSpace: TransformSpace = "world";
  // PART, not model (user direction 2026-09-04: "Set the part selection mode to
  // be default, instead of a model selection mode as it is now for all users").
  // Nothing persists this - it is session state, so this initializer IS the
  // default every user gets on every load, and there is no stored "model" for a
  // returning user to be stuck on. It also brings the Select tool into line with
  // the Grab tool, whose own Part/Model toggle has always defaulted to Part.
  // Two surfaces change with it, both deliberately: a viewport click and hover
  // resolve to the PART under the cursor rather than remapping to its model root
  // (`resolveSelectionModeSnapshotId`), and the Measure tool's box bounds that
  // part rather than the whole assembly (`BoundingBoxToolController`).
  private m_selectionMode: SelectionMode = "part";

  /**
   * @param m_permissions Omitted in a headless host, where every tool is
   * allowed: there is no user to deny. In the app it is always passed.
   */
  constructor(
    private readonly m_undoRedo?: IUndoRedo,
    private readonly m_permissions?: IPermissionsModel,
  ) {
    m_permissions?.onPermissionsChanged.on(() => this.demoteDeniedActiveTool());
  }

  get activeTool(): ToolId {
    return this.m_activeTool;
  }

  get transformMode(): TransformMode {
    return this.m_transformMode;
  }

  get transformSpace(): TransformSpace {
    return this.m_transformSpace;
  }

  get selectionMode(): SelectionMode {
    return this.m_selectionMode;
  }

  /**
   * Arming a tool the user may not use is refused rather than clamped.
   *
   * Upstream cannot reach this state at all — the button is deactivated — but
   * the web has PROGRAMMATIC arming paths that no hidden button guards, e.g.
   * `HierarchyView`'s Cutting Plane row arming "slice". The pointer is the
   * exception: it is the FALLBACK, and arming it while denied is the intended
   * inert state (see `K_FALLBACK_TOOL`).
   */
  setActiveTool(id: ToolId): void {
    if (this.m_activeTool === id) return;
    if (id !== K_FALLBACK_TOOL && !this.isAllowed(id)) return;
    this.m_activeTool = id;
    this.onActiveToolChanged.emit(id);
  }

  /**
   * Port of `FooterViewModel.ApplyEditorMode`'s tail
   * (`FooterViewModel.cs:1074-1095` + `:1093`): when the ARMED tool stops
   * being allowed, fall back to the pointer. A permission change must DISARM a
   * live tool, not leave a hidden one running — otherwise the pill loses its
   * Slice button while the slice gizmo stays attached with nothing on screen to
   * turn it off. Handing the presenter role to Spark at the start of a workflow
   * run is such a change, for every tool.
   */
  private demoteDeniedActiveTool(): void {
    const current = this.m_activeTool;
    if (current === K_FALLBACK_TOOL || this.isAllowed(current)) return;
    this.m_activeTool = K_FALLBACK_TOOL;
    this.onActiveToolChanged.emit(K_FALLBACK_TOOL);
  }

  private isAllowed(id: ToolId): boolean {
    if (!this.m_permissions) return true;
    return isToolbarToolAllowed(this.m_permissions, id);
  }

  setTransformMode(mode: TransformMode): void {
    if (this.m_transformMode === mode) return;
    this.m_transformMode = mode;
    this.onTransformModeChanged.emit(mode);
  }

  setTransformSpace(space: TransformSpace): void {
    if (this.m_transformSpace === space) return;
    this.m_transformSpace = space;
    this.onTransformSpaceChanged.emit(space);
  }

  setSelectionMode(mode: SelectionMode): void {
    if (this.m_selectionMode === mode) return;
    this.m_selectionMode = mode;
    this.onSelectionModeChanged.emit(mode);
  }

  /**
   * Undo / redo are refused for a user who may not undo
   * ({@link isUndoAllowed}), here rather than only on the buttons, because the
   * buttons are not the only caller: `useUndoRedoShortcuts` sends ⌘Z / ⌘⇧Z / ⌘Y
   * through this same method, and a button-only gate would leave the keyboard
   * open to a user whose Undo is hidden. A refused command is not re-emitted.
   *
   * While the presenter role is delegated to Spark the history itself is
   * suspended, so `undo` / `redo` no-op inside `IUndoRedo` — no run term here.
   */
  invoke(command: string): void {
    if (command === "undo" || command === "redo") {
      if (this.m_permissions && !isUndoAllowed(this.m_permissions)) return;
      if (command === "undo") this.m_undoRedo?.undo();
      else this.m_undoRedo?.redo();
    }
    this.onCommandInvoked.emit(command);
  }
}
